import React from "react";
import { motion } from "framer-motion";
import { Helmet } from "react-helmet-async";

const Owner = () => {
  return (
    <div className="bg-gray-50 text-gray-800 overflow-hidden font-sans">
      {/* SEO */}
      <Helmet>
        <title>Kwame Sarfo | Founder of Solonet Properties</title>
        <meta
          name="description"
          content="Meet Kwame Sarfo, the visionary founder of Solonet Properties. Leading real estate excellence in Ghana with integrity, innovation, and outstanding service."
        />
        <meta name="keywords" content="Kwame Sarfo, Kwame Sarfo, Solonet Properties, Real Estate Ghana, Founder, Property Developer, Real Estate Expert" />
        <meta name="robots" content="index, follow" />

        {/* Open Graph / Social Media */}
        <meta property="og:title" content="Kwame Sarfo | Founder of Solonet Properties" />
        <meta property="og:description" content="Leading Solonet Properties with integrity and innovation in Ghana’s real estate market." />
        <meta property="og:image" content="/images/owner-hero.jpg" />
        <meta property="og:url" content="https://solonetproperties.com/owner" />
        <meta property="og:type" content="website" />

        {/* Twitter Card */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Kwame Sarfo | Founder of Solonet Properties" />
        <meta name="twitter:description" content="Leading Solonet Properties with integrity and innovation in Ghana’s real estate market." />
        <meta name="twitter:image" content="/images/owner-hero.jpg" />
      </Helmet>

      {/* ===== HERO ===== */}
      <section className="relative w-full h-[70vh] flex items-center justify-center overflow-hidden">
        <img
          src="/images/header.jpg"
          alt="Kwame Sarfo, Founder"
          className="absolute w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-black/60 via-black/40 to-black/60"></div>

        {/* Floating shapes */}
        <motion.div
          className="absolute top-10 left-10 w-36 h-36 bg-blue-400 rounded-full mix-blend-multiply filter blur-3xl opacity-40"
          animate={{ y: [0, 30, 0] }}
          transition={{ duration: 6, repeat: Infinity }}
        />
        <motion.div
          className="absolute bottom-20 right-10 w-48 h-48 bg-purple-400 rounded-full mix-blend-multiply filter blur-3xl opacity-30"
          animate={{ y: [0, -40, 0] }}
          transition={{ duration: 8, repeat: Infinity }}
        />

        <motion.h1
          initial={{ opacity: 0, y: 50 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1 }}
          className="relative text-4xl md:text-6xl font-extrabold text-white text-center drop-shadow-xl"
        >
          Founder
        </motion.h1>
      </section>

      {/* ===== OWNER BIO ===== */}
      <section className="max-w-5xl mx-auto px-6 py-20">
        <div className="grid md:grid-cols-2 gap-12 items-center">
          {/* Image */}
          <motion.div
            className="relative"
            initial={{ opacity: 0, x: -50 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 1 }}
          >
            <img
              src="/images/owner.jpg"
              alt="Kwame Sarfo"
              className="rounded-3xl shadow-2xl object-cover w-full h-full"
            />
            {/* Decorative circles */}
            <div className="absolute -top-10 -left-10 w-24 h-24 bg-blue-200 rounded-full mix-blend-multiply filter blur-2xl opacity-50"></div>
            <div className="absolute -bottom-10 -right-10 w-32 h-32 bg-purple-200 rounded-full mix-blend-multiply filter blur-3xl opacity-40"></div>
          </motion.div>

          {/* Text */}
          <motion.div
            initial={{ opacity: 0, x: 50 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 1 }}
          >
            <h2 className="text-4xl font-bold text-gray-900 mb-6">
              Visionary Real Estate Leader
            </h2>
            <p className="text-gray-700 text-lg leading-relaxed mb-4">
              Kwame Sarfo is the founder and driving force behind <span className="font-semibold">Solonet Properties</span>, one of Ghana’s premier real estate companies. With a passion for innovation, integrity, and customer satisfaction, he has built a company that delivers exceptional property solutions for individuals, families, and businesses.
            </p>
            <p className="text-gray-700 text-lg leading-relaxed mb-6">
              Under his leadership, Solonet Properties has expanded its reach across Ghana, offering property sales, rentals, and management services while upholding the highest standards of transparency and professionalism.
            </p>

            <div className="flex flex-wrap gap-4 mt-6">
              <motion.a
                href="/contact"
                whileHover={{ scale: 1.05 }}
                className="px-6 py-3 bg-blue-600 text-white rounded-xl shadow-lg hover:bg-blue-700 transition"
              >
                Contact
              </motion.a>
              <motion.a
                href="/projects"
                whileHover={{ scale: 1.05 }}
                className="px-6 py-3 border-2 border-blue-600 text-blue-600 rounded-xl shadow hover:bg-blue-50 transition"
              >
                View Projects
              </motion.a>
            </div>
          </motion.div>
        </div>
      </section>

      {/* ===== HIGHLIGHTS / STATS ===== */}
      <section className="bg-white py-16">
        <div className="max-w-6xl mx-auto px-6 grid sm:grid-cols-3 gap-8 text-center">
          {[
            // { label: "Years of Experience", value: 10 },
            { label: "Projects Completed", value: 150 },
            { label: "Happy Clients", value: 600 },
          ].map((item, i) => (
            <motion.div
              key={i}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7 + i * 0.2 }}
              className="bg-gradient-to-r from-blue-100 to-purple-100 p-8 rounded-2xl shadow hover:shadow-xl transition"
            >
              <h3 className="text-3xl font-bold text-blue-600 mb-2">{item.value}+</h3>
              <p className="text-gray-700">{item.label}</p>
            </motion.div>
          ))}
        </div>
      </section>

      {/* ===== CTA ===== */}
      <section className="bg-gradient-to-r from-blue-600 to-purple-600 py-16 text-center text-white">
        <h2 className="text-3xl md:text-4xl font-bold mb-4">
          Start Your Real Estate Journey
        </h2>
        <p className="text-lg mb-6">
          Partner with a visionary who brings expertise and integrity to every property transaction.
        </p>
        <a
          href="/contact"
          className="px-8 py-3 bg-white text-blue-600 font-semibold rounded-lg shadow hover:bg-gray-100 transition"
        >
          Get in Touch
        </a>
      </section>
    </div>
  );
};

export default Owner;
